import axios from 'axios'
import { datadogService } from '@/services/datadog'
import { levelForApiError } from '@/services/datadogLogPolicy'
import { apiOrigin } from '@/utilities/url'

const client = axios.create({
  baseURL: apiOrigin(),
  timeout: 20000,
  xsrfHeaderName: 'X-CSRF-TOKEN',
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

// Request interceptor
client.interceptors.request.use((request) => {
  if (import.meta.env.MODE !== 'production') {
    console.log('request:', request)
  }
  return request
})

// Response interceptor for error tracking
client.interceptors.response.use(
  (response) => {
    if (import.meta.env.MODE !== 'production') {
      console.log('response:', response)
    }
    return response
  },
  (error) => {
    // Track API errors in Datadog
    // `status` はログレベルを表す予約キーで、context に入れると level（warn/error）を HTTP コードで上書きしてしまう。
    // `http_status` も Datadog 側の "Json log" パイプラインが status に写すので使わない。HTTP ステータスは response_status として送る
    const context: Record<string, any> = {
      error_type: 'api_error',
      url: error.config?.url,
      method: error.config?.method,
      baseURL: error.config?.baseURL,
      response_status: error.response?.status,
      response_status_text: error.response?.statusText,
      data: error.response?.data,
    }

    // Create a proper error message
    let errorMessage = 'API Request Failed'
    if (error.response) {
      errorMessage = `API Error ${error.response.status}: ${error.response.statusText}`
    } else if (error.request) {
      errorMessage = 'Network Error: No response received'
    } else {
      errorMessage = error.message
    }

    const apiError = new Error(errorMessage)
    apiError.stack = error.stack

    datadogService.trackError(apiError, context, levelForApiError(error))

    // Re-throw the error so it can be handled by the caller
    return Promise.reject(error)
  }
)

export default {
  async get(endpointURL: string, config = {}) {
    try {
      return await client.get(endpointURL, config)
    } catch (e) {
      console.error(e)
      throw e
    }
  },
  async post(endpointURL: string, params = {}, config = {}) {
    try {
      return await client.post(endpointURL, params, config)
    } catch (e) {
      console.error(e)
      throw e
    }
  },
  async put(endpointURL: string, params = {}, config = {}) {
    try {
      return await client.put(endpointURL, params, config)
    } catch (e) {
      console.error(e)
      throw e
    }
  },
  async delete(endpointURL: string, config = {}) {
    try {
      return await client.delete(endpointURL, config)
    } catch (e) {
      console.error(e)
      throw e
    }
  },
}
